import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { authClient } from '../../lib/auth-client'
import { BarChart3, Activity, TrendingUp, Calendar } from 'lucide-react'

interface UsageData {
  date: string
  requests: number
  apiCalls: number
  cost: number
}

export function UsagePage() {
  const { data: session } = authClient.useSession()
  const [usage, setUsage] = useState<UsageData[]>([])
  const [totalStats, setTotalStats] = useState({
    totalRequests: 0,
    totalApiCalls: 0,
    totalCost: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('7d')

  useEffect(() => {
    fetchUsageData()
  }, [timeRange])

  const fetchUsageData = async () => {
    try {
      const res = await fetch(`/api/v1/usage?range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setUsage(data.usage || [])
        setTotalStats({
          totalRequests: data.totalRequests || 0,
          totalApiCalls: data.totalApiCalls || 0,
          totalCost: data.totalCost || 0,
        })
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatCost = (micros: number) => {
    return `$${(micros / 1000000).toFixed(2)}`
  }

  return (
    <>
      <Helmet>
        <title>Usage | Twenty</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Usage</h1>
          <p className="text-muted-foreground">
            Monitor your API usage and costs
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          {['24h', '7d', '30d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                timeRange === range
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
            >
              {range === '24h' ? 'Last 24 hours' : range === '7d' ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-factory p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? '-' : totalStats.totalRequests.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Total Requests</p>
              </div>
            </div>
          </div>

          <div className="card-factory p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? '-' : totalStats.totalApiCalls.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">API Calls</p>
              </div>
            </div>
          </div>

          <div className="card-factory p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? '-' : formatCost(totalStats.totalCost)}
                </p>
                <p className="text-sm text-muted-foreground">Estimated Cost</p>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Table */}
        <div className="card-factory overflow-hidden">
          <div className="p-5 border-b border-border">
            <h2 className="font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Daily Breakdown
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse text-muted-foreground" aria-live="polite">Loading usage data…</div>
            </div>
          ) : usage.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No usage data for this period</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">Requests</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">API Calls</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">Cost</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((day) => (
                  <tr key={day.date} className="border-b border-border last:border-0">
                    <td className="py-3 px-6 text-sm">
                      {new Date(day.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="py-3 px-6 text-sm text-right">
                      {day.requests.toLocaleString()}
                    </td>
                    <td className="py-3 px-6 text-sm text-right">
                      {day.apiCalls.toLocaleString()}
                    </td>
                    <td className="py-3 px-6 text-sm text-right">
                      {formatCost(day.cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Note */}
        <div className="p-4 bg-secondary/50 rounded-sm">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Costs are estimates based on current pricing. 
            Final billing may vary. Fair use policy applies.
          </p>
        </div>
      </div>
    </>
  )
}
